import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import WebApp from '@twa-dev/sdk';
import { investmentService, InvestmentOpportunity } from '../services/investment.service';
import { Loader } from '../components/Loader';
import { useTranslation } from 'react-i18next';

// Маппинг для стандартных шагов и их английских заголовков
const STEP_TRANSLATION_KEYS: { [key: string]: string } = {
  'Prepare Web3 Wallet': 'PREPARE_WEB3_WALLET',
  'Connect Network': 'CONNECT_NETWORK',
  'Check Gas Balance': 'CHECK_GAS_BALANCE',
  'Prepare Assets': 'PREPARE_ASSETS'
};

const AssetDetailScreen: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [asset, setAsset] = React.useState<InvestmentOpportunity | null>(null);

  React.useEffect(() => {
    WebApp.ready();
    WebApp.setHeaderColor('secondary_bg_color');
  }, []);

  React.useEffect(() => {
    const fetchAsset = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        // Временно используем getBestOpportunities и фильтруем по id
        const opportunities = await investmentService.getBestOpportunities();
        const foundAsset = opportunities.find(opp => opp.id === id);
        if (foundAsset) {
          setAsset(foundAsset);
        }
      } catch (error) {
        console.error('Error fetching asset:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleLearnMore = () => {
    if (!asset || !asset.investment_steps) return;
    
    const step = asset.investment_steps.find(step => step.url);
    if (step?.url) {
      window.open(step.url, '_blank');
    }
  };

  const getStepContent = (step: { title: string; description: string }) => {
    // Определяем стандартный шаг по его английскому заголовку
    const translationKey = STEP_TRANSLATION_KEYS[step.title];
    
    if (translationKey) {
      // Если это стандартный шаг, используем перевод
      return {
        title: t(`ASSET_DETAIL_SCREEN.STEPS.${translationKey}.TITLE`),
        description: t(`ASSET_DETAIL_SCREEN.STEPS.${translationKey}.DESCRIPTION`)
      };
    }
    
    // Если это кастомный шаг, используем данные из БД
    return {
      title: step.title,
      description: step.description
    };
  };

  if (loading) {
    return (
      <div className="bg-tg-bg">
        <div className="text-center py-10 text-tg-hint text-base">
          <Loader />
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="bg-tg-bg">
        <div className="text-center py-10 text-[#FF0080] text-base">{t('ASSET_DETAIL_SCREEN.PROJECT_NOT_FOUND')}</div>
      </div>
    );
  }

  return (
    <div className="bg-tg-bg">
      <button 
        onClick={handleBack} 
        className="flex items-center gap-2 text-tg-hint bg-transparent border-none p-0 mb-5 cursor-pointer text-[15px] active:opacity-70"
      >
        <ArrowLeft size={20} />
        <span>{t('ASSET_DETAIL_SCREEN.BACK')}</span>
      </button>

      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl overflow-hidden bg-tg-secondary-bg">
            <img src={asset.logo_url} alt={asset.platform} className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-tg-text m-0 mb-1">{asset.platform}</h1>
            <div className="flex items-center gap-3 text-tg-hint text-[15px]">
              <span>{asset.type}</span>
              <div className="flex items-center gap-1.5">
                <img 
                  src={asset.network_logo_url} 
                  alt={asset.network} 
                  className="w-5 h-5 rounded-full"
                />
                <span>{asset.network}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-tg-secondary-bg rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
          <div className="text-tg-hint text-sm mb-1">{t('ASSET_DETAIL_SCREEN.STATS.APY')}</div>
          <div 
            className="text-2xl font-bold text-tg-text"
            style={{
              background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {asset.apy}%
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {asset.categories.map((category, index) => (
          <div key={index} className="bg-tg-secondary-bg px-3 py-1.5 rounded-[20px] text-sm text-tg-hint">
            {category}
          </div>
        ))}
      </div>

      <div className="bg-tg-secondary-bg rounded-2xl p-5 mb-6">
        <h2 className="text-xl font-bold text-tg-text m-0 mb-4">{t('ASSET_DETAIL_SCREEN.SETUP_STEPS')}</h2>
        <div className="flex flex-col gap-4">
          {asset.investment_steps.map((step, index) => {
            const content = getStepContent(step);
            return (
              <div key={index} className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-2xl bg-gradient-pink text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-tg-text m-0 mb-1">{content.title}</h3>
                  <p className="text-sm text-tg-hint m-0 leading-[1.4]">{content.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button 
        className="bg-gradient-pink text-white border-none rounded-2xl p-4 w-full text-base font-semibold cursor-pointer transition-transform duration-200 active:scale-[0.98]"
        onClick={handleLearnMore}
      >
        {t('ASSET_DETAIL_SCREEN.GO_TO_PROJECT')}
      </button>
    </div>
  );
};

export default AssetDetailScreen; 